import { cn } from '@/lib/utils';
import React, { useEffect, useState } from 'react';

import Graph from './Graph';
import GraphTabSelector from './dragDrop/GraphTabSelector';
import MobileTabBar, { MobileView } from './MobileTabBar';
import StixDownloadButton from './StixDownloadButton';
import { TerminalSlot } from './terminalHost';

interface MobileLayoutProps {
  children?: JSX.Element;
  className?: string;
}

/**
 * Single-panel mobile shell. The Chat panel arrives as `children`; Graph renders
 * directly and the Terminal is adopted from the shared host via `TerminalSlot`.
 * Panes stack with `absolute inset-0`. Only the Terminal pane needs real
 * dimensions while hidden (the persistent xterm renderer paints blank if ever
 * laid out at 0x0), so it hides via `visibility`; Chat and Graph hide via
 * `display: none` — React Flow marks each graph node `visibility: visible`,
 * which would otherwise bleed the graph through a `visibility: hidden` pane into
 * the chat. Graph lazy-mounts on first visit then stays mounted so its state
 * survives tab switches. Bottom tab bar switches panels.
 */
const MobileLayout: React.FC<MobileLayoutProps> = ({ children, className }) => {
  const [currentView, setCurrentView] = useState<MobileView>('chat');
  const [graphMounted, setGraphMounted] = useState(false);

  useEffect(() => {
    if (currentView === 'graph') setGraphMounted(true);
  }, [currentView]);

  useEffect(() => {
    if (currentView !== 'terminal') return;
    // The terminal pane keeps its dimensions while hidden (visibility), so
    // becoming the active view fires no ResizeObserver. Nudge a `resize` so the
    // persistent terminal re-fits and is correctly sized the first time it shows.
    window.dispatchEvent(new Event('resize'));
  }, [currentView]);

  const paneClass = (view: MobileView, keepLaidOut = false) =>
    cn(
      'absolute inset-0 h-full w-full',
      currentView === view
        ? 'visible z-10'
        : keepLaidOut
          ? 'invisible pointer-events-none'
          : 'hidden'
    );

  return (
    <div className={cn('flex h-full w-full flex-col bg-background', className)}>
      <div className="relative flex-1 min-h-0 overflow-hidden">
        <div className={paneClass('chat')}>{children}</div>
        <div className={paneClass('graph')}>
          {(graphMounted || currentView === 'graph') && (
            <div className="flex h-full w-full flex-col">
              <div className="flex flex-none items-center gap-1 overflow-x-auto border-b border-border bg-card px-2 py-1.5">
                <GraphTabSelector />
                <StixDownloadButton />
              </div>
              <div className="min-h-0 flex-1">
                <Graph className="h-full w-full" />
              </div>
            </div>
          )}
        </div>
        <div className={paneClass('terminal', true)}>
          <TerminalSlot className="h-full w-full" />
        </div>
      </div>

      <MobileTabBar
        className="flex-none"
        currentView={currentView}
        onViewChange={setCurrentView}
      />
    </div>
  );
};

export default MobileLayout;
